import { useEffect, useRef } from 'react';

/**
 * Componente que renderiza el header + navigation del theme_templates JSON.
 * Usa innerHTML (no createContextualFragment) para garantizar que
 * los scripts embebidos NO se ejecuten.
 * @param {Function} onReady - Callback que se ejecuta cuando el HTML fue inyectado.
 */
export default function ThemeHeader({ header, onReady }) {
  const markerRef = useRef(null);

  useEffect(() => {
    const marker = markerRef.current;
    if (!marker) return;

    const parent = marker.parentNode;
    const html = (header || '');

    // Limpiar nodos previamente inyectados
    while (marker.nextSibling && marker.nextSibling.__themeHeader) {
      parent.removeChild(marker.nextSibling);
    }

    if (!html) return;

    let cancelled = false;

    function injectHeader() {
      if (cancelled) return;

      // Parsear con DOMParser para tener control total (no ejecuta scripts)
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');

      // Remover todos los <script> del HTML parseado
      doc.querySelectorAll('script').forEach((s) => s.remove());

      // Insertar cada nodo después del marcador
      const nodes = Array.from(doc.body.childNodes);
      let insertPoint = marker;
      nodes.forEach((node) => {
        const imported = document.adoptNode(node);
        imported.__themeHeader = true;
        parent.insertBefore(imported, insertPoint.nextSibling);
        insertPoint = imported;
      });

      if (onReady) onReady();
    }

    // Esperar a que el CSS del theme esté aplicado antes de inyectar el HTML
    // del menú. Así se evita el FOUC (el menú se veía desplegado un instante
    // hasta que el CSS lo colapsaba). Si la promesa no existe (fallback),
    // se inyecta de inmediato.
    if (window.__themeCssReady && typeof window.__themeCssReady.then === 'function') {
      window.__themeCssReady.then(injectHeader);
    } else {
      injectHeader();
    }

    return () => {
      cancelled = true;
      while (marker.nextSibling && marker.nextSibling.__themeHeader) {
        parent.removeChild(marker.nextSibling);
      }
    };
  }, [header, onReady]);

  return <span ref={markerRef} style={{ display: 'none' }} />;
}